import { Sparkles } from "lucide-react";
import { type FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { InspirationTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { InspirationDetailLayout } from "web_myplann/Inspiration/Components/InspirationDetailLayout";
import { useInspirationCtas } from "web_myplann/Inspiration/Hooks/useInspirationCtas";
import { useOpenGoalFormFromInspiration } from "web_myplann/Inspiration/Hooks/useOpenGoalFormFromInspiration";
import { AffordabilityEditSheet } from "web_myplann/Inspiration/OwnHome/Components/AffordabilityEditSheet";
import { ExpensesSheet } from "web_myplann/Inspiration/OwnHome/Components/ExpensesSheet";
import { OwnHomeContent } from "web_myplann/Inspiration/OwnHome/Components/OwnHomeContent";
import { OwnHomeEstimateSheet } from "web_myplann/Inspiration/OwnHome/Components/OwnHomeEstimateSheet";
import {
	OWN_HOME_GOAL_CATEGORY,
	OWN_HOME_IMAGE,
} from "web_myplann/Inspiration/OwnHome/constants";
import { useOwnHomeGoalRecap } from "web_myplann/Inspiration/OwnHome/Hooks/useOwnHomeGoalRecap";
import { useOwnHomeJourney } from "web_myplann/Inspiration/OwnHome/Hooks/useOwnHomeJourney";
import { mapOwnHomeGoalPreset } from "web_myplann/Inspiration/OwnHome/Mappers/mapOwnHomeGoalPreset";

const PageResources = Resources.Inspiration.OwnHome;

export const OwnHomeInspirationPage: FC = () => {
	const { t } = useResource();
	const navigate = useNavigate();
	const { openGoalForm } = useOpenGoalFormFromInspiration();
	const {
		inputs,
		context,
		isLoading,
		activeSheet,
		openSheet,
		closeSheet,
		submitInputs,
	} = useOwnHomeJourney();

	const isCold = !context || context.state === "cold";

	const goToFinances = () => navigate(Routing.Finances.Intro.path());

	const goalPreset = useMemo(
		() => (context ? mapOwnHomeGoalPreset(context) : null),
		[context],
	);
	const recap = useOwnHomeGoalRecap(goalPreset, context?.rates);

	const createGoal = () => {
		if (!goalPreset) return;

		openGoalForm(
			{
				category: OWN_HOME_GOAL_CATEGORY,
				totalValue: goalPreset.totalValue,
				ownFunds: goalPreset.ownFunds,
				loanAmount: goalPreset.loanAmount,
			},
			InspirationTypeEnum.OwnHome,
		);
	};

	const { goalRecap, primaryCta, secondaryCta } = useInspirationCtas({
		isCold,
		coldCta: {
			label: t(PageResources.cta),
			icon: Sparkles,
			disabled: !context,
			onClick: () => openSheet("quick"),
		},
		goalRecap: recap,
		createGoal: {
			label: t(PageResources.Result.createGoal),
			onClick: createGoal,
		},
		advisor: {
			label: t(PageResources.Result.contactAdvisor),
			message: t(PageResources.Result.advisorMessage),
		},
	});

	return (
		<>
			<InspirationDetailLayout
				scrollResetKey={isCold}
				image={OWN_HOME_IMAGE}
				category={t(PageResources.category)}
				title={t(PageResources.title)}
				subtitle={t(PageResources.subtitle)}
				goalRecap={goalRecap}
				primaryCta={primaryCta}
				secondaryCta={secondaryCta}
				isLoading={isLoading}
			>
				<OwnHomeContent
					context={context}
					isLoading={isLoading}
					onEditAffordability={() => openSheet("affordability-edit")}
					onAddExpenses={() => openSheet("expenses")}
				/>
			</InspirationDetailLayout>

			<OwnHomeEstimateSheet
				open={activeSheet === "quick"}
				onClose={closeSheet}
				onSubmit={submitInputs}
				onSaveToBilance={goToFinances}
			/>

			<ExpensesSheet
				open={activeSheet === "expenses"}
				initial={inputs.expenses}
				onClose={closeSheet}
				onSubmit={(expenses) => submitInputs({ expenses })}
				onSaveToBilance={goToFinances}
			/>

			<AffordabilityEditSheet
				open={activeSheet === "affordability-edit"}
				initialIncome={inputs.income}
				initialExpenses={inputs.expenses}
				showExpenses={context?.state === "full"}
				onClose={closeSheet}
				onSubmit={submitInputs}
			/>
		</>
	);
};
